import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import { ClienteService } from "../../service/cliente_service";

// Importar os componentes das abas
import AbaEmpresa from "./abas/AbaEmpresa";
import AbaProdutos from "./abas/AbaProdutos";

function ClientesCad() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [abaAtiva, setAbaAtiva] = useState("empresa");
	const [loading, setLoading] = useState(false);
	const [salvando, setSalvando] = useState(false);

	// Estados para cada aba
	const [dadosEmpresa, setDadosEmpresa] = useState({});
	const [produtos, setProdutos] = useState([]);

	// Referência para função de adicionar produto da AbaProdutos
	const [addProdutoHandler, setAddProdutoHandler] = useState(null);

	// Pegar unidade distribuidora do localStorage
	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	useEffect(() => {
		if (id) {
			loadCliente(id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const formatCpfCnpj = (value) => {
		if (!value) return "";
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	};

	const loadCliente = async (clienteId) => {
		setLoading(true);
		try {
			const clienteService = new ClienteService();
			const data = await clienteService.getById(clienteId);

			// Carregar dados da empresa
			setDadosEmpresa({
				id: data.id,
				nome: data.nome || "",
				nomeFantasia: data.nomeFantasia || "",
				cpfCnpj: formatCpfCnpj(data.cpfCnpj || ""),
				ieRg: data.ieRg || "",
				email: data.email || "",
				telefone: data.telefone || "",
				dataEntradaBase: data.dataEntradaBase ? data.dataEntradaBase.split('T')[0] : "",
				cep: data.endereco?.cep || "",
				uf: data.endereco?.uf || "",
				cidade: data.endereco?.cidade || "",
				bairro: data.endereco?.bairro || "",
				logradouro: data.endereco?.logradouro || "",
				latitude: data.endereco?.latitude || "",
				longitude: data.endereco?.longitude || "",
			});

			// Carregar produtos
			const produtosFormatados = (data.produtos || []).map((produto) => ({
				id: produto.id,
				cultivo: produto.cultivo,
				unidadeMedida: produto.unidadeMedida,
				codigoBarras: produto.codigoBarras || "",
				cultivoId: produto.cultivoId,
				unidadeMedidaId: produto.unidadeMedidaId,
			}));
			setProdutos(produtosFormatados);
		} catch (error) {
			console.error("Erro ao carregar cliente:", error);
			alert(error.message || "Erro ao carregar dados do cliente");
		} finally {
			setLoading(false);
		}
	};

	const handleSalvar = async () => {
		try {
			// Validações básicas
			if (!dadosEmpresa.nome || !dadosEmpresa.cpfCnpj) {
				alert("Preencha os campos obrigatórios: Nome e CPF/CNPJ");
				setAbaAtiva("empresa");
				return;
			}

			setSalvando(true);

			// Preparar dados para envio
			const clienteData = {
				id: dadosEmpresa.id || undefined,
				unidadeDistribuidoraId: unidadeDistribuidoraId,
				nome: dadosEmpresa.nome,
				nomeFantasia: dadosEmpresa.nomeFantasia,
				cpfCnpj: dadosEmpresa.cpfCnpj ? dadosEmpresa.cpfCnpj.replace(/\D/g, "") : "",
				ieRg: dadosEmpresa.ieRg,
				email: dadosEmpresa.email,
				telefone: dadosEmpresa.telefone,
				dataEntradaBase: dadosEmpresa.dataEntradaBase || undefined,
				endereco: {
					cep: dadosEmpresa.cep,
					uf: dadosEmpresa.uf,
					cidade: dadosEmpresa.cidade,
					bairro: dadosEmpresa.bairro,
					logradouro: dadosEmpresa.logradouro,
					latitude: dadosEmpresa.latitude ? parseFloat(dadosEmpresa.latitude) : undefined,
					longitude: dadosEmpresa.longitude ? parseFloat(dadosEmpresa.longitude) : undefined,
				},
				produtos: [],
			};

			// Processar produtos
			if (produtos && produtos.length > 0) {
				clienteData.produtos = produtos.map((produto) => ({
					cultivoId: produto.cultivoId,
					unidadeMedidaId: produto.unidadeMedidaId,
					codigoBarras: produto.codigoBarras || undefined,
				}));
			}

			// Salvar no backend
			const clienteService = new ClienteService();

			if (id) {
				// Atualizar
				await clienteService.update(clienteData);
				alert("Cliente atualizado com sucesso!");
			} else {
				// Criar
				await clienteService.save(clienteData);
				alert("Cliente cadastrado com sucesso!");
			}

			navigate("/clientes");
		} catch (error) {
			console.error("Erro ao salvar cliente:", error);
			alert(error.message || "Erro ao salvar cliente");
		} finally {
			setSalvando(false);
		}
	};

	const handleAddProdutos = () => {
		if (abaAtiva === "produtos" && addProdutoHandler) {
			addProdutoHandler();
		}
	};

	const renderAbaContent = () => {
		switch (abaAtiva) {
			case "empresa":
				return (
					<AbaEmpresa
						dados={dadosEmpresa}
						setDados={setDadosEmpresa}
					/>
				);
			case "produtos":
				return (
					<AbaProdutos
						produtos={produtos}
						setProdutos={setProdutos}
						onAddProdutoClick={setAddProdutoHandler}
					/>
				);
			default:
				return null;
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="clientes">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={() => navigate("/clientes")} sx={{ color: "#666" }}>
							<ArrowBackIcon />
						</IconButton>
						<Typography
							variant="h5"
							sx={{
								color: "#666",
								fontWeight: 500,
								fontSize: "20px",
							}}
						>
							Cadastro de Cliente
						</Typography>
					</Box>

					{/* Container com fundo branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "4px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							overflow: "hidden",
						}}
					>
						{/* Header com abas e botão */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 6,
								pt: 4,
								pb: 0,
							}}
						>
							{/* Navegação das abas */}
							<Box sx={{ display: "flex", gap: 0.5 }}>
								<Button
									onClick={() => setAbaAtiva("empresa")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "empresa" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "empresa" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "empresa" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Empresa
								</Button>
								<Button
									onClick={() => setAbaAtiva("produtos")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "produtos" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "produtos" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "produtos" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Produtos
								</Button>
							</Box>

							{/* Botão Adicionar produtos */}
							{abaAtiva === "produtos" && (
								<Button
									variant="contained"
									onClick={handleAddProdutos}
									sx={{
										bgcolor: "#1a9e8f",
										"&:hover": {
											bgcolor: "#138a7d",
											boxShadow: "0 2px 4px rgba(23, 162, 184, 0.3)",
										},
										textTransform: "none",
										px: 3,
										py: 1.25,
										fontSize: "15px",
										fontWeight: 500,
										borderRadius: "4px",
										boxShadow: "none",
									}}
								>
									Adicionar produto
								</Button>
							)}
						</Box>

						{/* Conteúdo da aba */}
						<Box sx={{ px: 6, py: 4, minHeight: "400px" }}>{renderAbaContent()}</Box>

						{/* Botão Cadastrar no canto inferior direito */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								px: 6,
								pb: 4,
								pt: 0,
							}}
						>
							<Button
								variant="contained"
								onClick={handleSalvar}
								disabled={salvando}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										boxShadow: "0 2px 6px rgba(23, 162, 184, 0.4)",
									},
									"&:disabled": {
										bgcolor: "#ccc",
									},
									textTransform: "none",
									px: 5,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
									boxShadow: "none",
								}}
							>
								{salvando ? "Salvando..." : id ? "Atualizar" : "Cadastrar"}
							</Button>
						</Box>
					</Box>
			</Box>
		</AppLayout>
	);
}

export default ClientesCad;
